import { useState } from "react";

import { SampleDataOverlay } from "./DiscoveryHubSampleState";
import {
  S7_QUAD_LEGEND,
  S7_QUAD_LEGEND_DOT,
  S7_QUAD_LEGEND_ITEM,
  S7_QUADRANT,
  S7_QUADRANT_H,
  S7_QUADRANT_PERIOD,
  S7_QUADRANT_SUB,
} from "../../Utils/Helper/discoveryHubUi";

/** Midpoint of the 1–5 axes so the four quadrants are equal. */
const GAP_SPLIT = 3;

type QuadrantPoint = {
  account_id?: string;
  account_name?: string;
  p1: number;
  p2: number | null;
  gap?: string;
};

const W = 500;
const M = 50;
const PLOT_TOP = 8;
const PLOT_BOTTOM = 284;
const PLOT_HEIGHT = PLOT_BOTTOM - PLOT_TOP;
const SVG_H = 320;
const QUAD_LABEL_BOTTOM_Y = PLOT_BOTTOM - 11;
const TICK_Y = PLOT_BOTTOM + 16;
const X_AXIS_LABEL_Y = PLOT_BOTTOM + 28;

const xFromP1 = (p1: number) => M + ((p1 - 1) / 4) * (W - M - 10);
const yFromP2 = (p2: number) => PLOT_BOTTOM - ((p2 - 1) / 4) * PLOT_HEIGHT;

const colorByGap: Record<string, string> = {
  "Aligned-High": "#15803d",
  "Over-claim": "#d97706",
  "Under-report": "#d97706",
  "At-risk": "#dc2626",
};

const SAMPLE_POINTS: QuadrantPoint[] = [
  {
    account_id: "s1",
    account_name: "Acme Corp",
    p1: 4.2,
    p2: 4.0,
    gap: "Aligned-High",
  },
  {
    account_id: "s2",
    account_name: "Beta Inc",
    p1: 3.8,
    p2: 2.1,
    gap: "Over-claim",
  },
  {
    account_id: "s3",
    account_name: "Gamma Ltd",
    p1: 1.9,
    p2: 3.5,
    gap: "Under-report",
  },
  {
    account_id: "s4",
    account_name: "Delta Co",
    p1: 2.0,
    p2: 1.8,
    gap: "At-risk",
  },
  {
    account_id: "s5",
    account_name: "Epsilon SA",
    p1: 4.5,
    p2: 3.9,
    gap: "Aligned-High",
  },
];

type Props = {
  points: QuadrantPoint[];
  period: string;
  gapSplit?: number;
};

const EngagementGapQuadrant = ({ points, period }: Props) => {
  const [hover, setHover] = useState<{
    name: string;
    cx: number;
    cy: number;
  } | null>(null);

  const midX = xFromP1(GAP_SPLIT);
  const midY = yFromP2(GAP_SPLIT);

  const isSample = !points.some(pt => pt.p2 != null);
  const displayPoints = isSample ? SAMPLE_POINTS : points;

  const completePts = displayPoints.filter(pt => pt.p2 != null);
  const p1OnlyPts = displayPoints.filter(pt => pt.p2 == null);

  return (
    <div className={S7_QUADRANT}>
      <div className={S7_QUADRANT_H}>
        Engagement Gap Quadrant
        <span className={S7_QUADRANT_PERIOD}>{period}</span>
      </div>

      <div className="px-4 pb-4 pt-3">
        <div className={S7_QUADRANT_SUB}>
          Each dot = one account · Poll 1 (self-report) vs Poll 2 (team
          feedback)
        </div>

        <SampleDataOverlay
          active={isSample}
          compact
          message="Preview of engagement gap — real data appears once polls are submitted."
        >
          <div className="relative w-full" style={{ height: 320 }}>
            <svg
              className="pointer-events-auto block h-full w-full"
              viewBox={`0 0 ${W} ${SVG_H}`}
              preserveAspectRatio="none"
            >
              {/* Plot background — white like Recharts canvas */}
              <rect
                x={M}
                y={PLOT_TOP}
                width={W - M - 10}
                height={PLOT_HEIGHT}
                fill="#fff"
                stroke="#e0e6ed"
              />
              {/* Vertical grid lines at each tick — matching CartesianGrid */}
              {[1, 2, 3, 4, 5].map(t => (
                <line
                  key={`vg-${t}`}
                  x1={xFromP1(t)}
                  y1={PLOT_TOP}
                  x2={xFromP1(t)}
                  y2={PLOT_BOTTOM}
                  stroke="#e0e6ed"
                  strokeDasharray="3 3"
                />
              ))}
              {/* Horizontal grid lines at each tick */}
              {[1, 2, 3, 4, 5].map(t => (
                <line
                  key={`hg-${t}`}
                  x1={M}
                  y1={yFromP2(t)}
                  x2={W - 10}
                  y2={yFromP2(t)}
                  stroke="#e0e6ed"
                  strokeDasharray="3 3"
                />
              ))}
              {/* Quadrant split lines */}
              <line
                x1={midX}
                y1={PLOT_TOP}
                x2={midX}
                y2={PLOT_BOTTOM}
                stroke="#9ca3af"
                strokeDasharray="4 3"
                strokeWidth={1.5}
              />
              <line
                x1={M}
                y1={midY}
                x2={W - 10}
                y2={midY}
                stroke="#9ca3af"
                strokeDasharray="4 3"
                strokeWidth={1.5}
              />
              {completePts.length > 0 && (
                <>
                  <text
                    x={(M + midX) / 2}
                    y={PLOT_TOP + 20}
                    fontSize={12}
                    fill="#d97706"
                    textAnchor="middle"
                    fontWeight={600}
                  >
                    UNDER-REPORT
                  </text>
                  <text
                    x={(midX + W - 10) / 2}
                    y={PLOT_TOP + 20}
                    fontSize={12}
                    fill="#15803d"
                    textAnchor="middle"
                    fontWeight={600}
                  >
                    ALIGNED-HIGH
                  </text>
                  <text
                    x={(M + midX) / 2}
                    y={QUAD_LABEL_BOTTOM_Y}
                    fontSize={12}
                    fill="#dc2626"
                    textAnchor="middle"
                    fontWeight={600}
                  >
                    AT-RISK
                  </text>
                  <text
                    x={(midX + W - 10) / 2}
                    y={QUAD_LABEL_BOTTOM_Y}
                    fontSize={12}
                    fill="#d97706"
                    textAnchor="middle"
                    fontWeight={600}
                  >
                    OVER-CLAIM
                  </text>
                </>
              )}
              {/* Axis borders — light, matching Recharts axis line style */}
              <line
                x1={M}
                y1={PLOT_BOTTOM}
                x2={W - 10}
                y2={PLOT_BOTTOM}
                stroke="#d1d5db"
                strokeWidth={1}
              />
              <line
                x1={M}
                y1={PLOT_TOP}
                x2={M}
                y2={PLOT_BOTTOM}
                stroke="#d1d5db"
                strokeWidth={1}
              />
              <text
                x={W / 2}
                y={X_AXIS_LABEL_Y}
                fontSize={10}
                fill="#6b7280"
                textAnchor="middle"
                fontWeight={600}
              >
                Poll 1 — Sponsor Self-Report (1 → 5)
              </text>
              <text
                x={14}
                y={(PLOT_TOP + PLOT_BOTTOM) / 2}
                fontSize={10}
                fill="#6b7280"
                textAnchor="middle"
                fontWeight={600}
                transform={`rotate(-90 14 ${(PLOT_TOP + PLOT_BOTTOM) / 2})`}
              >
                Poll 2 — Team Feedback (1 → 5)
              </text>
              {[1, 2, 3, 4, 5].map(t => (
                <text
                  key={`x-${t}`}
                  x={xFromP1(t)}
                  y={TICK_Y}
                  fontSize={10}
                  fill="#6B7280"
                  fontWeight={600}
                  textAnchor="middle"
                >
                  {t}
                </text>
              ))}
              {[1, 2, 3, 4, 5].map(t => (
                <text
                  key={`y-${t}`}
                  x={M - 6}
                  y={yFromP2(t) + 3}
                  fontSize={10}
                  fill="#6B7280"
                  fontWeight={600}
                  textAnchor="end"
                >
                  {t}
                </text>
              ))}
              {completePts.map(pt => {
                const cx = xFromP1(pt.p1);
                const cy = yFromP2(pt.p2 as number);
                const color = colorByGap[pt.gap ?? ""] || "#6b7280";
                const name = pt.account_name || "Account";
                return (
                  <g
                    key={pt.account_id ?? `${pt.p1}-${pt.p2}`}
                    onMouseEnter={() => setHover({ name, cx, cy })}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: "pointer" }}
                  >
                    <circle cx={cx} cy={cy} r={12} fill="transparent" />
                    <circle
                      cx={cx}
                      cy={cy}
                      r={6}
                      fill={color}
                      fillOpacity={0.75}
                      stroke="#fff"
                      strokeWidth={1.5}
                    />
                  </g>
                );
              })}
              {p1OnlyPts.map(pt => {
                const cx = xFromP1(pt.p1);
                const cy = PLOT_BOTTOM;
                const name = pt.account_name || "Account";
                return (
                  <g
                    key={pt.account_id ?? `p1only-${pt.p1}`}
                    onMouseEnter={() => setHover({ name, cx, cy })}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: "pointer" }}
                  >
                    <circle cx={cx} cy={cy} r={12} fill="transparent" />
                    <circle
                      cx={cx}
                      cy={cy}
                      r={6}
                      fill="#fff"
                      stroke="#9ca3af"
                      strokeWidth={1.5}
                      strokeDasharray="3,2"
                    />
                  </g>
                );
              })}
            </svg>
            {hover && (
              <div
                className="pointer-events-none absolute z-20 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-900 shadow-lg"
                style={{
                  left: `${(hover.cx / W) * 100}%`,
                  top: `${(hover.cy / SVG_H) * 100}%`,
                  transform: "translate(-50%, calc(-100% - 8px))",
                }}
              >
                {hover.name}
              </div>
            )}
          </div>

          <div className={S7_QUAD_LEGEND}>
            {completePts.length > 0 && (
              <>
                <div className={S7_QUAD_LEGEND_ITEM}>
                  <span
                    className={S7_QUAD_LEGEND_DOT}
                    style={{ background: "#15803d" }}
                  />
                  Aligned-High: strong on both
                </div>
                <div className={S7_QUAD_LEGEND_ITEM}>
                  <span
                    className={S7_QUAD_LEGEND_DOT}
                    style={{ background: "#d97706" }}
                  />
                  Over-claim: P1 ↑ / P2 ↓
                </div>
                <div className={S7_QUAD_LEGEND_ITEM}>
                  <span
                    className={S7_QUAD_LEGEND_DOT}
                    style={{ background: "#d97706" }}
                  />
                  Under-report: P1 ↓ / P2 ↑
                </div>
                <div className={S7_QUAD_LEGEND_ITEM}>
                  <span
                    className={S7_QUAD_LEGEND_DOT}
                    style={{ background: "#dc2626" }}
                  />
                  At-risk: both ↓
                </div>
              </>
            )}
            {p1OnlyPts.length > 0 && (
              <div className={`${S7_QUAD_LEGEND_ITEM} col-span-2`}>
                <span
                  className={S7_QUAD_LEGEND_DOT}
                  style={{
                    border: "1.5px dashed #9ca3af",
                    background: "transparent",
                  }}
                />
                Poll 1 only — awaiting team feedback (shown on x-axis)
              </div>
            )}
          </div>
        </SampleDataOverlay>
      </div>
    </div>
  );
};

export default EngagementGapQuadrant;
